import React, { useEffect, useRef, useState } from "react";
import { useIsNearViewport } from "./useIsNearViewport";

/**
 * A muted, looping, autoplaying video that is not fetched until it is about to scroll into view. The
 * grow-a-tree recording is ~2 MB and its poster 74 KB; requested at page load they compete with the hero
 * for bandwidth on a phone, so neither is requested until the section comes near (callers give the video
 * its size, so the empty element already takes the poster's place). `autoplay` would make browsers fetch
 * the file regardless of `preload`, hence the src is added at that point instead.
 */
export function LazyAutoplayVideo({
  src,
  poster,
  ...videoProps
}: { src: string } & Omit<
  React.VideoHTMLAttributes<HTMLVideoElement>,
  "src" | "autoPlay" | "muted" | "loop" | "playsInline"
>) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isNearViewport = useIsNearViewport(videoRef);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const visibilityObserver = new IntersectionObserver((entries) => {
      setIsVisible(entries.some((entry) => entry.isIntersecting));
    });
    visibilityObserver.observe(videoElement);
    return () => visibilityObserver.disconnect();
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !isNearViewport) return;
    // React sets `muted` as a DOM property rather than an attribute, which browsers' autoplay policies
    // don't honor, so playback is muted and started imperatively.
    videoElement.muted = true;
    const updatePlayback = () => {
      if (isVisible && document.visibilityState === "visible") {
        videoElement.play().catch(() => {});
      } else {
        videoElement.pause();
      }
    };
    updatePlayback();
    document.addEventListener("visibilitychange", updatePlayback);
    return () => {
      document.removeEventListener("visibilitychange", updatePlayback);
      videoElement.pause();
    };
  }, [isNearViewport, isVisible]);

  return (
    <video
      ref={videoRef}
      src={isNearViewport ? src : undefined}
      poster={isNearViewport ? poster : undefined}
      // For the page's <noscript> copy, where nothing comes near (see DeferredImage).
      data-deferred-poster={isNearViewport ? undefined : poster}
      preload="none"
      muted
      loop
      playsInline
      {...videoProps}
    />
  );
}
